import { Mail, Phone, MapPin, Clock, Send } from "lucide-react";
import PageShell from "./PageShell";
import PageHero from "./PageHero";
import Reveal from "./Reveal";
import RelatedPages from "./RelatedPages";
import { contactInfo, admissionsInfo, ORIG } from "../mock";

export default function ContactPage() {
  return (
    <PageShell>
      {({ openEnquire }) => (
        <>
          <PageHero
            eyebrow="Reach Out"
            title="Contact"
            italic="GD Goenka Signature."
            image={`${ORIG}/home-banner1a.webp`}
            blurb="We'd love to hear from you. Our admissions team responds within one working day."
          />

          <section className="bg-white py-14 md:py-20">
            <div className="max-w-[1200px] mx-auto px-5 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14">
              {/* Left: details */}
              <Reveal variant="left">
                <div className="eyebrow mb-5">Admissions Office</div>
                <h2 className="h-section text-[var(--ink)]">
                  Let&apos;s start a
                  <span className="block serif-italic gold-text">conversation.</span>
                </h2>
                <span className="gold-divider-thin block my-6" />

                <div className="space-y-5">
                  <a href={`mailto:${contactInfo.email}`} className="flex items-start gap-4 group">
                    <span className="w-11 h-11 border border-[var(--gold)] flex items-center justify-center shrink-0 group-hover:bg-[var(--gold)] transition-colors">
                      <Mail size={17} className="text-[var(--gold)] group-hover:text-[var(--navy)] transition-colors" />
                    </span>
                    <div>
                      <div className="text-[11px] tracking-[0.24em] uppercase text-[var(--muted)]">Email</div>
                      <div className="serif text-[18px] md:text-[20px] text-[var(--ink)] group-hover:text-[var(--gold)] transition-colors">{contactInfo.email}</div>
                    </div>
                  </a>
                  <a href={`tel:${contactInfo.phone.replace(/\s/g, "")}`} className="flex items-start gap-4 group">
                    <span className="w-11 h-11 border border-[var(--gold)] flex items-center justify-center shrink-0 group-hover:bg-[var(--gold)] transition-colors">
                      <Phone size={17} className="text-[var(--gold)] group-hover:text-[var(--navy)] transition-colors" />
                    </span>
                    <div>
                      <div className="text-[11px] tracking-[0.24em] uppercase text-[var(--muted)]">Phone</div>
                      <div className="serif text-[18px] md:text-[20px] text-[var(--ink)] group-hover:text-[var(--gold)] transition-colors">{contactInfo.phone}</div>
                      <div className="serif text-[15px] md:text-[16px] text-[var(--ink)]/70">{contactInfo.altPhone}</div>
                    </div>
                  </a>
                  <div className="flex items-start gap-4">
                    <span className="w-11 h-11 border border-[var(--gold)] flex items-center justify-center shrink-0">
                      <MapPin size={17} className="text-[var(--gold)]" />
                    </span>
                    <div>
                      <div className="text-[11px] tracking-[0.24em] uppercase text-[var(--muted)]">Address</div>
                      <div className="serif text-[16.5px] md:text-[18px] text-[var(--ink)] leading-snug max-w-[400px]">{contactInfo.address}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="w-11 h-11 border border-[var(--gold)] flex items-center justify-center shrink-0">
                      <Clock size={17} className="text-[var(--gold)]" />
                    </span>
                    <div>
                      <div className="text-[11px] tracking-[0.24em] uppercase text-[var(--muted)]">Office Hours</div>
                      <div className="serif text-[16.5px] md:text-[18px] text-[var(--ink)]">Mon – Sat · 8:00 am — 4:30 pm</div>
                    </div>
                  </div>
                </div>

                <div className="mt-8">
                  <p className="text-[12px] tracking-[0.24em] uppercase text-[var(--gold-light)]">
                    {admissionsInfo.open} · {admissionsInfo.grades} · {admissionsInfo.session}
                  </p>
                </div>
              </Reveal>

              {/* Right: CTA card */}
              <Reveal variant="right" delay={100}>
                <div className="relative p-7 md:p-10" style={{ background: "var(--navy)" }}>
                  <div className="hidden md:block absolute -top-3 -left-3 w-14 h-14 border-l border-t border-[var(--gold)]" />
                  <div className="hidden md:block absolute -bottom-3 -right-3 w-14 h-14 border-r border-b border-[var(--gold)]" />
                  <Send size={28} className="text-[var(--gold)]" strokeWidth={1.4} />
                  <h3 className="serif text-white text-[24px] md:text-[32px] leading-tight mt-5">
                    Begin your
                    <span className="block serif-italic gold-text">enquiry.</span>
                  </h3>
                  <span className="gold-divider-thin block my-5" />
                  <p className="text-white/75 text-[15px] leading-[1.8] mb-6">
                    Use our quick enquiry form. We respond within one working day.
                  </p>
                  <button onClick={openEnquire} className="btn-gold" style={{ background: "var(--gold)", color: "var(--navy-deep)" }}>
                    Submit an Enquiry
                  </button>
                </div>
              </Reveal>
            </div>
          </section>

          {/* Map */}
          <section className="bg-[var(--cream)] py-14 md:py-20">
            <div className="max-w-[1300px] mx-auto px-5 md:px-10">
              <Reveal>
                <div className="text-center max-w-[700px] mx-auto mb-8 md:mb-10">
                  <div className="eyebrow mb-4">Find Us</div>
                  <h2 className="h-section text-[var(--ink)]">
                    Sohna Road,
                    <span className="serif-italic gold-text"> Gurugram.</span>
                  </h2>
                </div>
              </Reveal>
              <div className="relative aspect-[16/9] overflow-hidden border border-[rgba(184,148,86,0.25)]">
                <iframe
                  title="GDGSS Map"
                  src="https://www.google.com/maps?q=GD+Goenka+Signature+School+Sohna+Road+Gurugram&output=embed"
                  className="w-full h-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </section>

          <RelatedPages current="/contact-us.html" />
        </>
      )}
    </PageShell>
  );
}
